/* eslint-disable react/jsx-props-no-spreading */
import React, { cloneElement, isValidElement, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlertCircle,
  Check,
  CheckCircle,
  ChevronDown,
  Circle,
  CircleHelp,
  Eye,
  EyeOff,
  FolderOpen,
  Info,
  JapaneseYen,
  Lock,
  Pencil,
  Plus,
  Menu as MenuIcon,
  MessageCircle,
  Moon,
  Phone,
  Search,
  Settings,
  Shield,
  Smartphone,
  Sun,
  User,
  X,
} from 'lucide-react';
const cx = (...classes) => classes.filter(Boolean).join(' ');

const iconMap = {
  check: Check,
  checkmark: Check,
  'check circle': CheckCircle,
  close: X,
  remove: X,
  user: User,
  sidebar: MenuIcon,
  bars: MenuIcon,
  settings: Settings,
  shield: Shield,
  moon: Moon,
  sun: Sun,
  lock: Lock,
  search: Search,
  'warning circle': AlertCircle,
  'exclamation circle': AlertCircle,
  info: Info,
  mobile: Smartphone,
  phone: Phone,
  folder: FolderOpen,
  'folder open': FolderOpen,
  'folder open outline': FolderOpen,
  question: CircleHelp,
  comment: MessageCircle,
  dropdown: ChevronDown,
  yen: JapaneseYen,
  edit: Pencil,
  pencil: Pencil,
  add: Plus,
  plus: Plus,
  eye: Eye,
  'eye-off': EyeOff,
  eyeOff: EyeOff,
  circle: Circle,
};

const colorClass = color => ({
  red: 'text-red-600',
  green: 'text-green-600',
  blue: 'text-blue-600',
  orange: 'text-orange-500',
  grey: 'text-[var(--owner-text-muted)]',
  gray: 'text-[var(--owner-text-muted)]',
  black: 'text-[var(--owner-text)]',
})[color] || '';

export const Icon = ({ name, color, size, style, className }) => {
  const IconComponent = iconMap[name] || CircleHelp;
  const pixelSize = ({ small: 14, large: 28, huge: 76, massive: 96 })[size] || 18;
  return <IconComponent aria-hidden className={cx('inline-block align-middle', colorClass(color), className)} size={pixelSize} style={style} />;
};

Icon.propTypes = {
  className: PropTypes.string,
  color: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.string,
  style: PropTypes.object,
};

export const Button = ({ basic, circular, color, disabled, floated, fluid, icon, children, className, style, type, ...props }) => (
  <button
    type={type || 'button'}
    disabled={disabled}
    className={cx(
      'inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-bold leading-5 shadow-sm transition hover:brightness-95 active:scale-[0.98]',
      basic ? 'border border-[var(--owner-border-strong)] bg-[var(--owner-surface)] text-[var(--owner-text)]' : 'border border-transparent bg-[var(--tenant-primary)] text-white',
      color === 'red' && !basic && 'bg-red-600',
      color === 'green' && !basic && 'bg-green-600',
      color === 'blue' && !basic && 'bg-blue-600',
      color === 'orange' && !basic && 'bg-orange-500',
      circular && 'rounded-full',
      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      fluid && 'w-full',
      floated === 'left' && 'float-left',
      floated === 'right' && 'float-right',
      className
    )}
    style={style}
    {...props}
  >
    {typeof icon === 'string' && <Icon name={icon} />}
    {children}
  </button>
);

Button.propTypes = {
  basic: PropTypes.bool,
  children: PropTypes.node,
  circular: PropTypes.bool,
  className: PropTypes.string,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  floated: PropTypes.string,
  fluid: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  style: PropTypes.object,
  type: PropTypes.string,
};

export const Container = ({ children, textAlign, className, style }) => (
  <div
    className={cx('mx-auto w-full max-w-[var(--tenant-max-width,1200px)] px-4 sm:px-6', textAlign === 'center' && 'text-center', className)}
    style={style}
  >
    {children}
  </div>
);

Container.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  textAlign: PropTypes.string,
};

const headerSizeClass = as => ({
  h1: 'text-[32px]',
  h2: 'text-[28px]',
  h3: 'text-[22px]',
  h4: 'text-lg',
  h5: 'text-[15px]',
})[as] || 'text-[22px]';

export const Header = ({ as: Element = 'h2', children, className, icon, inverted, size, style, textAlign, ...props }) => (
  <Element
    className={cx(
      'm-0 font-bold leading-tight tracking-normal',
      !size && headerSizeClass(Element),
      size === 'medium' && 'text-lg leading-snug',
      inverted ? 'text-white' : 'text-[var(--owner-text)]',
      icon && 'text-center',
      textAlign === 'center' && 'text-center',
      className
    )}
    style={style}
    {...props}
  >
    {children}
  </Element>
);

Header.propTypes = {
  as: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  icon: PropTypes.bool,
  inverted: PropTypes.bool,
  size: PropTypes.string,
  style: PropTypes.object,
  textAlign: PropTypes.string,
};

Header.Subheader = ({ children, className, style }) => <div className={cx('mt-1.5 text-sm font-normal leading-6 text-[var(--owner-text-muted)]', className)} style={style}>{children}</div>;
Header.Content = ({ children, className, style }) => <span className={className} style={style}>{children}</span>;

export const Divider = ({ className, style }) => <hr className={cx('my-4 border-[var(--owner-border)]', className)} style={style} />;

export const Segment = ({ as: Element = 'div', basic, children, className, compact, style }) => (
  <Element className={cx(!basic && 'rounded-xl border border-[var(--owner-border)] bg-[var(--owner-surface)] shadow-sm', compact ? 'inline-block p-3' : 'p-4', className)} style={style}>
    {children}
  </Element>
);

Segment.propTypes = {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  basic: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  compact: PropTypes.bool,
  style: PropTypes.object,
};

export const Card = ({ children, className, style }) => (
  <div className={cx('overflow-hidden rounded-2xl border border-[var(--owner-border)] bg-[var(--owner-surface)] shadow-sm transition-shadow hover:shadow-md', className)} style={style}>
    {children}
  </div>
);
Card.Content = ({ children, className, style }) => <div className={cx('p-4', className)} style={style}>{children}</div>;
Card.Header = ({ children, className, style }) => <div className={cx('text-lg font-bold leading-snug text-[var(--owner-text)]', className)} style={style}>{children}</div>;
Card.Meta = ({ children, className, style }) => <div className={cx('mt-0.5 text-sm text-[var(--owner-text-muted)]', className)} style={style}>{children}</div>;
Card.Description = ({ children, className, style }) => <div className={cx('mt-1 text-sm text-[var(--owner-text-muted)]', className)} style={style}>{children}</div>;
Card.Extra = ({ children, className, style }) => <div className={cx('border-t border-[var(--owner-border)] p-4', className)} style={style}>{children}</div>;
Card.Group = ({ children, items, className, style }) => (
  <div className={cx('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', className)} style={style}>
    {items ?
      items.map((item, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Card key={index}>
          <Card.Content>
            {item.header && <Card.Header>{item.header}</Card.Header>}
            {item.meta && <Card.Meta>{item.meta}</Card.Meta>}
            {item.description && <Card.Description>{item.description}</Card.Description>}
          </Card.Content>
          {item.extra && <Card.Extra>{item.extra}</Card.Extra>}
        </Card>
      ))
      : children}
  </div>
);

const normalizeChange = (onChange, name) => event => {
  if (onChange) {
    onChange(event, { name, value: event.target.value, checked: event.target.checked });
  }
};

export const Input = ({ ref, error, fluid, icon, id, label, name, onChange, required, style, className, input, meta, size, ...props }) => {
  const hasError = error || (meta && meta.touched && meta.error);
  const fieldProps = input || {};
  const changeHandler = input ? input.onChange : normalizeChange(onChange, name);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    inputRef,
    focus: () => inputRef.current && inputRef.current.focus(),
  }));

  return (
    <span className={cx('inline-flex flex-col gap-1', fluid && 'w-full')}>
      {label && (
        <label className="text-sm font-semibold text-[var(--owner-text)]" htmlFor={id || name}>
          {label}
          {required && <span className="ml-1 text-red-600">*</span>}
        </label>
      )}
      <span className={cx('relative inline-flex items-center', fluid && 'w-full')}>
        <input
          aria-invalid={!!hasError}
          className={cx(
            'min-h-10 rounded-lg border bg-[var(--owner-surface)] px-3 text-sm leading-5 shadow-sm outline-none transition-colors focus:border-[var(--tenant-primary)] focus:ring-2 focus:ring-[var(--owner-focus-ring)]',
            size === 'large' && 'min-h-12 text-base',
            hasError ? 'border-red-500 text-red-900' : 'border-[var(--owner-border-strong)] text-[var(--owner-text)]',
            icon && 'pr-10',
            fluid && 'w-full',
            className
          )}
          id={id}
          name={name}
          onChange={changeHandler}
          ref={inputRef}
          required={required}
          style={style}
          {...fieldProps}
          {...props}
        />
        {typeof icon === 'string' && (
          <span className="pointer-events-none absolute right-3 text-[var(--owner-text-muted)]">
            <Icon name={icon} />
          </span>
        )}
      </span>
    </span>
  );
};

Input.propTypes = {
  className: PropTypes.string,
  error: PropTypes.any,
  fluid: PropTypes.bool,
  id: PropTypes.string,
  icon: PropTypes.string,
  input: PropTypes.object,
  label: PropTypes.node,
  meta: PropTypes.object,
  name: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  size: PropTypes.string,
  style: PropTypes.object,
};

export const TextArea = ({ name, onChange, style, className, ...props }) => (
  <textarea
    className={cx('min-h-24 w-full rounded-lg border border-[var(--owner-border-strong)] bg-[var(--owner-surface)] px-3 py-2 text-sm leading-6 text-[var(--owner-text)] shadow-sm outline-none transition-colors focus:border-[var(--tenant-primary)] focus:ring-2 focus:ring-[var(--owner-focus-ring)]', className)}
    name={name}
    onChange={normalizeChange(onChange, name)}
    style={style}
    {...props}
  />
);

// When `multiple`, the select reports all selected values via event.target.selectedOptions
// rather than event.target.value; emit them as an array so consumers can use the same shape
// the legacy Semantic-UI Dropdown produced.
const normalizeMultiChange = (onChange, name) => event => {
  if (!onChange) {return;}
  const values = Array.from(event.target.selectedOptions || []).map(opt => opt.value);
  onChange(event, { name, value: values });
};

export const Dropdown = ({ options, value, defaultValue, placeholder, fluid, name, ariaLabel, onChange, style, className, disabled, multiple, size }) => {
  let selected;
  if (multiple) {
    if (Array.isArray(value)) {selected = value;}
    else if (Array.isArray(defaultValue)) {selected = defaultValue;}
    else {selected = [];}
  }
  else {
    selected = value === undefined || value === null ? defaultValue || '' : value;
  }
  return (
    <div
      className={cx(
        'relative inline-flex',
        fluid && 'w-full'
      )}
    >
      <select
        className={cx(
          'w-full appearance-none min-h-10 rounded-lg border border-[var(--owner-border-strong)] bg-[var(--owner-surface)] text-sm leading-5 text-[var(--owner-text)] shadow-sm outline-none transition-all cursor-pointer',
          'hover:border-[var(--owner-accent-border)]',
          'focus:border-[var(--tenant-primary)] focus:ring-2 focus:ring-[var(--owner-focus-ring)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          multiple ? 'px-3 py-2 h-auto' : 'pl-3 pr-10',
          className
        )}
        disabled={disabled}
        multiple={multiple}
        name={name}
        aria-label={ariaLabel}
        onChange={multiple ? normalizeMultiChange(onChange, name) : normalizeChange(onChange, name)}
        size={size}
        style={style}
        value={selected}
      >
        {!multiple && placeholder && <option value="">{placeholder}</option>}
        {(options || []).map(option => (
          <option key={option.key || option.value || option.text} value={option.value} disabled={option.disabled}>
            {option.text || option.content || option.value}
          </option>
        ))}
      </select>
      {!multiple && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--owner-text-muted)]">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" strokeWidth="2.5"
               strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  className: PropTypes.string,
  defaultValue: PropTypes.any,
  disabled: PropTypes.bool,
  fluid: PropTypes.bool,
  multiple: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.node,
  size: PropTypes.number,
  style: PropTypes.object,
  value: PropTypes.any,
};

export const Checkbox = ({ checked, label, name, onChange, style, toggle, disabled }) => (
  <label className={cx('inline-flex cursor-pointer items-center gap-2 text-sm leading-5 text-[var(--owner-text)]', toggle && 'rounded-full')} style={style}>
    <input
      checked={!!checked}
      className="h-4 w-4 cursor-pointer accent-[var(--tenant-primary)]"
      disabled={disabled}
      name={name}
      onChange={event => onChange && onChange(event, { name, checked: event.target.checked })}
      type="checkbox"
    />
    <span>{label}</span>
  </label>
);

export const Radio = ({ checked, label, name, onChange, value, style }) => (
  <label className="inline-flex cursor-pointer items-center gap-2 text-sm leading-5 text-[var(--owner-text)]" style={style}>
    <input
      checked={checked}
      className="h-4 w-4 accent-[var(--tenant-primary)]"
      name={name}
      onChange={event => onChange && onChange(event, { name, value })}
      type="radio"
      value={value}
    />
    <span>{label}</span>
  </label>
);

export const Message = ({ children, error, positive, warning, size, style, className, header, list }) => {
  const toneClass = error ?
    'border-red-200 bg-red-50 text-red-800'
    : positive ?
      'border-green-200 bg-green-50 text-green-800'
      : warning ?
        'border-yellow-200 bg-yellow-50 text-yellow-800'
        : 'border-[var(--owner-info-border)] bg-[var(--owner-info-bg)] text-[var(--owner-info-text)]';

  const hasList = Array.isArray(list) && list.length > 0;

  return (
    <div
      className={cx('rounded-xl border px-4 py-3 text-sm leading-6', toneClass, size === 'massive' && 'text-base', className)}
      role={error ? 'alert' : 'status'}
      style={style}
    >
      {header && <div className="mb-1 font-bold">{header}</div>}
      {hasList && (
        <ul className="ml-5 list-disc space-y-0.5">
          {/* eslint-disable-next-line @eslint-react/no-array-index-key */}
          {list.map((item, index) => <li key={index}>{item}</li>)}
        </ul>
      )}
      {children}
    </div>
  );
};

export const Loader = ({ active = true, inline, size, style, children }) => (
  active ? (
    <span className={cx('inline-flex items-center justify-center gap-2', !inline && 'w-full py-6')} style={style}>
      <span
        aria-hidden="true"
        className={cx(
          'inline-block animate-spin rounded-full border-2 border-[var(--owner-border)] border-t-[var(--tenant-primary)]',
          size === 'small' ? 'h-4 w-4' : 'h-7 w-7'
        )}
      />
      {children && <span>{children}</span>}
    </span>
  ) : null
);

export const Dimmer = ({ active, children }) => active ? <div className="fixed inset-0 z-50 grid place-items-center bg-[color-mix(in_srgb,var(--owner-page-bg)_78%,transparent)] backdrop-blur-sm">{children}</div> : children;

export const Image = ({ src, alt, style, className }) => <img src={src} alt={alt} className={className} style={style} />;

export const Label = ({ as: Element = 'span', children, basic, color, onClick, style, className }) => (
  <Element
    className={cx(
      'inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold leading-5',
      basic ? 'border border-[var(--owner-border-strong)] bg-[var(--owner-surface)]' : 'bg-[var(--owner-surface-muted)]',
      colorClass(color),
      onClick && 'cursor-pointer',
      className
    )}
    onClick={onClick}
    style={style}
  >
    {children}
  </Element>
);

export const List = ({ children, className, items, size, style }) => (
  <ul className={cx('space-y-2 text-sm leading-6 text-[var(--owner-text)]', size === 'large' && 'text-base', className)} style={style}>
    {items ? items.map((item, index) => (
      // eslint-disable-next-line @eslint-react/no-array-index-key
      <li key={index}>{item}</li>
    )) : children}
  </ul>
);
List.Item = ({ children, className, content, icon, style }) => (
  <li className={cx((icon || content) && 'flex items-start gap-2', className)} style={style}>
    {icon && <span className="mt-0.5 inline-flex shrink-0">{icon}</span>}
    {content ? <span>{content}</span> : children}
  </li>
);
List.Icon = props => <Icon {...props} />;
List.Content = ({ children, className, style }) => <div className={className} style={style}>{children}</div>;
List.Header = ({ as: Element = 'div', children, className, style, ...props }) => (
  <Element className={cx('font-semibold text-[var(--owner-text)]', className)} style={style} {...props}>{children}</Element>
);
List.Description = ({ as: Element = 'div', children, className, style, ...props }) => (
  <Element className={cx('text-sm text-[var(--owner-text-muted)]', className)} style={style} {...props}>{children}</Element>
);
List.List = ({ children, className, style }) => <ul className={cx('mt-2 space-y-1', className)} style={style}>{children}</ul>;

export const Grid = ({ children, className, columns, stackable, style }) => (
  <div className={cx('grid gap-4', columns === 2 && 'md:grid-cols-2', stackable && 'grid-cols-1', className)} style={style}>{children}</div>
);
Grid.Row = ({ children, centered, className, style }) => <div className={cx('flex flex-wrap gap-4', centered && 'justify-center', className)} style={style}>{children}</div>;
Grid.Column = ({ children, className, style, verticalAlign }) => <div className={cx(verticalAlign === 'middle' && 'self-center', className)} style={style}>{children}</div>;
export const GridRow = Grid.Row;
export const GridColumn = Grid.Column;

export const Table = ({ children, className, style }) => <table className={cx('w-full border-collapse text-left text-sm leading-6 text-[var(--owner-text)]', className)} style={style}>{children}</table>;
Table.Header = ({ children }) => <thead className="border-b border-[var(--owner-border)] bg-[var(--owner-surface-muted)]">{children}</thead>;
Table.Body = ({ children }) => <tbody className="divide-y divide-[var(--owner-border)]">{children}</tbody>;
Table.Footer = ({ children }) => <tfoot className="border-t border-[var(--owner-border)] bg-[var(--owner-surface-muted)]">{children}</tfoot>;
Table.Row = ({ children, className, style }) => <tr className={cx('align-top', className)} style={style}>{children}</tr>;
Table.Cell = ({ children, colSpan, className, style }) => <td colSpan={colSpan} className={cx('px-4 py-3', className)} style={style}>{children}</td>;
Table.HeaderCell = ({ children, className, style }) => <th className={cx('px-4 py-3 text-xs font-bold uppercase tracking-wide text-[var(--owner-text-muted)]', className)} style={style}>{children}</th>;

export const Modal = ({ open, children, onClose }) => open ? (
  <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" role="presentation" onClick={onClose}>
    {/* max-h/overflow-y so content taller than the viewport (e.g. a small phone
        in landscape) scrolls within the modal instead of getting clipped by
        the fixed viewport bounds. */}
    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
    <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-y-auto rounded-2xl bg-[var(--owner-surface)] text-[var(--owner-text)] shadow-2xl" role="dialog" onClick={event => event.stopPropagation()}>{children}</div>
  </div>
) : null;
Modal.Content = ({ children, style }) => <div className="p-6" style={style}>{children}</div>;
// Stack full-width on mobile rather than relying on flex-wrap alone - a
// single long label (e.g. RecoveryCodesModal's "I've saved these codes")
// has no min-width/shrink handling of its own, so even wrapped onto its own
// row it could still be wider than the viewport and overflow. Stacking each
// button at full width guarantees it can't, regardless of label length.
// [&>*]:w-full forces this directly on every child - align-items: stretch
// alone doesn't do it, since the HeroUI Button has its own intrinsic width
// that resists being stretched by its flex parent, leaving buttons left-
// aligned at their natural (narrow) width instead of filling the row.
Modal.Actions = ({ children, style }) => <div className="flex flex-col gap-2 rounded-b-2xl border-t border-[var(--owner-border)] p-4 [&>*]:w-full sm:flex-row sm:flex-wrap sm:justify-end sm:[&>*]:w-auto" style={style}>{children}</div>;
Modal.Header = ({ children }) => <div className="border-b border-[var(--owner-border)] p-4 text-lg font-bold leading-snug text-[var(--owner-text)]">{children}</div>;

export const Sidebar = ({ children, visible, style }) => (
  <aside className={cx('fixed inset-y-0 left-0 z-40 w-80 max-w-[85vw] bg-zinc-950 text-white shadow-2xl transition-transform', visible ? 'translate-x-0' : '-translate-x-full')} style={style}>
    {children}
  </aside>
);
Sidebar.Pushable = ({ as: Element = 'div', children, style }) => <Element style={style}>{children}</Element>;
Sidebar.Pusher = ({ children, style }) => <div style={style}>{children}</div>;

export const Menu = ({ children, className, style, vertical, inverted, secondary }) => (
  <nav
    className={cx(
      'flex',
      vertical ? 'flex-col gap-0.5' : 'gap-1',
      !secondary && !inverted && 'rounded-md border border-[var(--owner-border)] bg-[var(--owner-surface)]',
      inverted && 'text-white',
      className
    )}
    style={style}
  >
    {children}
  </nav>
);

Menu.Item = ({ children, name, onClick, style, className }) => (
  <button
    type="button"
    className={cx(
      'px-4 py-3 text-left font-semibold transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:bg-white/10 cursor-pointer',
      'w-full block',
      className
    )}
    onClick={onClick}
    style={style}
  >
    {children || name}
  </button>
);

export const Tab = ({ panes = [], activeIndex }) => {
  const [internalIndex, setInternalIndex] = useState(activeIndex ?? 0);
  const renderedPanes = useMemo(() => panes.filter(Boolean), [panes]);
  const requestedIndex = typeof activeIndex === 'number' ? activeIndex : internalIndex;
  const selectedIndex = requestedIndex >= 0 && requestedIndex < renderedPanes.length ? requestedIndex : 0;
  const selectedPane = renderedPanes[selectedIndex];
  return (
    <div className="w-full">
      <div className="mb-5 flex flex-wrap gap-2 border-b border-[var(--owner-border)]">
        {renderedPanes.map((pane, index) => {
          const item = pane.menuItem;
          const isSelected = index === selectedIndex;
          const label = isValidElement(item) ? item : (
            <button type="button">
              {item}
            </button>
          );
          return cloneElement(label, {
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key: index,
            onClick: event => {
              if (label.props.onClick) {label.props.onClick(event);}
              setInternalIndex(index);
            },
            className: cx(
              'rounded-t-xl border border-b-0 px-4 py-2 text-sm font-semibold shadow-sm transition-colors cursor-pointer',
              isSelected ? 'border-[var(--owner-border-strong)] bg-[var(--owner-surface)] text-[var(--tenant-primary)]' : 'border-transparent bg-[var(--owner-surface-muted)] text-[var(--owner-text-muted)] hover:brightness-95',
              label.props.className
            ),
          });
        })}
      </div>
      {selectedPane && selectedPane.render && selectedPane.render()}
    </div>
  );
};
Tab.Pane = ({ children, style }) => <div className="px-4 py-3" style={style}>{children}</div>;

export const Step = ({ children, active, completed, disabled, onClick }) => (
  <button
    type="button"
    className={cx('flex flex-1 items-center gap-2 rounded-lg border border-[var(--owner-border)] px-3 py-2 text-left text-sm leading-5 text-[var(--owner-text)]', active && 'border-[var(--tenant-primary)] bg-[color-mix(in_srgb,var(--tenant-primary)_14%,var(--owner-surface))]', completed && 'border-green-300 bg-green-50', disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
);
Step.Group = ({ children, style }) => <div className="flex gap-2" style={style}>{children}</div>;
Step.Content = ({ children }) => <span>{children}</span>;
Step.Title = ({ children }) => <span className="font-semibold">{children}</span>;

export const Portal = ({ open, children }) => open ? children : null;
export const Transition = ({ visible, children }) => visible ? children : null;
export const Popup = ({ children, trigger }) => trigger || children || null;

export const Form = ({ children, onSubmit, style, className }) => (
  <form
    className={className}
    onSubmit={event => {
      // Semantic UI's Form prevented the native submit; preserve that so dispatched
      // requests aren't aborted by a full-page navigation (see 2FA verify bug).
      event.preventDefault();
      if (onSubmit) {
        onSubmit(event);
      }
    }}
    style={style}
  >
    {children}
  </form>
);
Form.Field = ({ children, inline, error, style, className }) => <div className={cx('mb-4', inline && 'md:flex md:items-center md:gap-4', error && 'text-red-600', className)} style={style}>{children}</div>;
Form.Group = ({ children, style }) => <div className="mb-4" style={style}>{children}</div>;
Form.Input = props => <Input {...props} />;
Form.Button = props => <Button {...props} />;

export default {
  Button,
  Card,
  Checkbox,
  Container,
  Dimmer,
  Divider,
  Dropdown,
  Form,
  Grid,
  GridColumn,
  GridRow,
  Header,
  Icon,
  Image,
  Input,
  Label,
  List,
  Loader,
  Menu,
  Message,
  Modal,
  Popup,
  Portal,
  Radio,
  Segment,
  Sidebar,
  Step,
  Tab,
  Table,
  TextArea,
  Transition,
};
